import { matchPath } from "react-router-dom";
import type { UserProfile } from "../../types/auth";
import { PERMISSIONS, hasPermission, type Permission } from "../../utils/permissions";
import type { TourKeyOverrides } from "./tourEngine";
import type { RosterBoardKind } from "./activeRosterBoard";

/**
 * A page tour the user can replay from the Guided tours list in the sidebar.
 *
 * `featureKey` and the resolved page tour key must match the page's own `useTour` call (its page
 * tour, or its data tour for an entry that replays that alone), or the page will not pick the replay
 * up. In an entry, `dataTourKey: null` means "replay this tour alone, with nothing chained after
 * it". It does not mean the same as in a `useTour` call, where it switches the page's data tour off,
 * so never copy an entry's keys into the hook. `requiresPermission` is the same Page Access key the nav item and the route's
 * `ProtectedRoute` use, so the list never offers a page this user cannot open.
 */
export interface GuidedTourEntry {
  id: string;
  /** The nav label of the page, so the list reads the same as the sidebar. */
  label: string;
  featureKey: string;
  keys?: TourKeyOverrides;
  requiresPermission?: Permission;
  /** Where the tour runs, given where the user is now. Null when it cannot be reached from here. */
  pathFor: (currentPathname: string) => string | null;
  /**
   * Only offered on this kind of roster board. A recruiting board runs its own Players tab tour
   * and has no Lineups tab, so the roster-board entries for those are hidden on it, and the
   * recruiting entry on a roster board. Entries without it are offered on both.
   */
  rosterBoardKind?: RosterBoardKind;
}

const fixed = (path: string) => () => path;

/**
 * The Roster Planning tour lives on one board's Players tab (`/rosters/:id/players`), so it has no
 * fixed address. It is offered only while the user is inside a board, and runs on that board.
 */
const rosterBoardPath =
  (tab: "players" | "depth-chart" | "pitching-staff" | "lineups") =>
  (currentPathname: string): string | null => {
    const match = matchPath("/rosters/:id/*", currentPathname);
    const id = match?.params.id;
    if (!id || id === "roster-images") return null;
    // matchPath hands the id back decoded, so it is encoded again on the way into a path.
    return `/rosters/${encodeURIComponent(id)}/${tab}`;
  };

/**
 * Every page-level tour, in sidebar order. Flow, wizard and modal tours (roster import, the build
 * itinerary wizard) are deliberately absent: they only make sense mid-task and still auto-run the
 * first time. The schedule's tournament catalog is absent too, because it is a dialog opened from a
 * button on /schedule rather than anything a route or link can reach.
 *
 * A new page with a `useTour` call adds its entry here, with the same key overrides.
 */
export const GUIDED_TOURS: readonly GuidedTourEntry[] = [
  /*
    Players and "Open a player" are two entries over one page: the page tour, and the data tour on
    its own. `dataTourKey: null` on the first is what stops its replay running the second straight
    after it. Both are run by the Players page's one `useTour("players")` call.
  */
  {
    id: "players",
    label: "Players",
    featureKey: "players",
    keys: { dataTourKey: null },
    pathFor: fixed("/players"),
  },
  {
    id: "players.data",
    label: "Open a player",
    featureKey: "players",
    keys: { pageTourKey: "players.data", dataTourKey: null },
    pathFor: fixed("/players"),
  },
  {
    id: "calls",
    label: "Calls",
    featureKey: "calls",
    requiresPermission: PERMISSIONS.calls,
    pathFor: fixed("/calls"),
  },
  {
    id: "video",
    label: "Video",
    featureKey: "video",
    requiresPermission: PERMISSIONS.video,
    pathFor: fixed("/video"),
  },
  {
    id: "notes",
    label: "Latest Notes",
    featureKey: "notes",
    requiresPermission: PERMISSIONS.notes,
    pathFor: fixed("/latest-notes"),
  },
  /*
    The board list at /rosters. The nav calls this page "Roster Planning", but that label already
    names the in-board tour below, so the list entry gets a name of its own.
  */
  {
    id: "rosters.index",
    label: "Roster boards",
    featureKey: "rosters",
    keys: { pageTourKey: "rosters.index", dataTourKey: null },
    requiresPermission: PERMISSIONS.rosters,
    pathFor: fixed("/rosters"),
  },
  {
    id: "rosters",
    label: "Roster Planning",
    featureKey: "rosters",
    requiresPermission: PERMISSIONS.rosters,
    pathFor: rosterBoardPath("players"),
    rosterBoardKind: "roster",
  },
  /*
    A recruiting board's Players tab runs the recruiting-board feature's tours instead of the
    roster ones (ScholarshipPlayersTab picks the feature by the board's purpose).
  */
  {
    id: "recruiting-board",
    label: "Recruiting Board",
    featureKey: "recruiting-board",
    requiresPermission: PERMISSIONS.rosters,
    pathFor: rosterBoardPath("players"),
    rosterBoardKind: "recruiting",
  },
  /*
    The board's other three views each have a page tour of their own, run by that tab's useTour with
    the same key overrides as here. Like Roster Planning, they are offered only inside a board.
  */
  {
    id: "rosters.depth",
    label: "Roster depth chart",
    featureKey: "rosters",
    keys: { pageTourKey: "rosters.depth", dataTourKey: null },
    requiresPermission: PERMISSIONS.rosters,
    pathFor: rosterBoardPath("depth-chart"),
  },
  {
    id: "rosters.pitching",
    label: "Roster pitching staff",
    featureKey: "rosters",
    keys: { pageTourKey: "rosters.pitching", dataTourKey: null },
    requiresPermission: PERMISSIONS.rosters,
    pathFor: rosterBoardPath("pitching-staff"),
  },
  {
    id: "rosters.lineups",
    label: "Roster lineups",
    featureKey: "rosters",
    keys: { pageTourKey: "rosters.lineups", dataTourKey: null },
    requiresPermission: PERMISSIONS.rosters,
    pathFor: rosterBoardPath("lineups"),
    // A recruiting board has no Lineups tab.
    rosterBoardKind: "roster",
  },
  {
    id: "schedule",
    label: "Schedule",
    featureKey: "schedule",
    requiresPermission: PERMISSIONS.schedule,
    pathFor: fixed("/schedule"),
  },
  {
    id: "duplicates",
    label: "Merge Manager",
    featureKey: "duplicates",
    requiresPermission: PERMISSIONS.duplicates,
    pathFor: fixed("/duplicates"),
  },
];

export interface AvailableGuidedTour {
  entry: GuidedTourEntry;
  path: string;
}

/**
 * The entries this user can replay from here: the page is open to them, reachable from the
 * current location, and its page tour has a step they can see. The page tour specifically, because a
 * replay from the list always starts there: a feature whose only visible tour is its data tour is
 * not offered.
 */
export const availableGuidedTours = (
  currentPathname: string,
  profile: UserProfile | undefined,
  hasPageTour: (featureKey: string, keys?: TourKeyOverrides) => boolean,
  entries: readonly GuidedTourEntry[] = GUIDED_TOURS,
  /** The kind of roster board open now (`useActiveRosterBoardKind`), null when none is. */
  rosterBoardKind: RosterBoardKind | null = null
): AvailableGuidedTour[] =>
  entries.flatMap((entry) => {
    if (entry.requiresPermission && !hasPermission(profile, entry.requiresPermission)) return [];
    // Until the board has loaded its kind is unknown, so neither kind-specific entry is offered.
    if (entry.rosterBoardKind && entry.rosterBoardKind !== rosterBoardKind) return [];
    if (!hasPageTour(entry.featureKey, entry.keys)) return [];
    const path = entry.pathFor(currentPathname);
    return path ? [{ entry, path }] : [];
  });

/**
 * Whether this user has any tour the list could ever offer, wherever they are.
 *
 * Deliberately blind to the current route, unlike `availableGuidedTours`: it decides whether the
 * sidebar shows the "Guided tours" item at all, and an item that came and went as the coach moved
 * between pages would be worse than one that sometimes opens on a short list.
 */
export const hasAnyGuidedTour = (
  profile: UserProfile | undefined,
  hasPageTour: (featureKey: string, keys?: TourKeyOverrides) => boolean,
  entries: readonly GuidedTourEntry[] = GUIDED_TOURS
): boolean =>
  entries.some(
    (entry) =>
      (!entry.requiresPermission || hasPermission(profile, entry.requiresPermission)) &&
      hasPageTour(entry.featureKey, entry.keys)
  );
